import { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Download,
  ExternalLink,
  Loader2,
  MoreHorizontal,
  RefreshCw,
  Trash2,
  User,
} from "lucide-react";
import type { BlockType } from "@/types/block";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import Papa from "papaparse";

interface PageResponseResponseType {
  block?: string;
  value: string;
  htmlId?: string;
}

interface PageResponse {
  _id: string;
  createdAt: string;
  formHtmlId?: string;
  lead?: string;
  responses: PageResponseResponseType[];
}

interface Page {
  _id: string;
  title: string;
  blocks: BlockType[];
}

function findBlockById(blocks: BlockType[], blockId: string): BlockType | null {
  for (const block of blocks) {
    if (block.id === blockId) {
      return block;
    }
    if (block.blocks) {
      const found = findBlockById(block.blocks, blockId);
      if (found) return found;
    }
    const columns = block.data?.columns || block.columns;
    if (Array.isArray(columns)) {
      for (const column of columns) {
        if (Array.isArray(column)) {
          const found = findBlockById(column, blockId);
          if (found) return found;
        }
      }
    }
  }
  return null;
}

/** If the column name includes "phone" (label, html id, or block id), normalize to E.164; otherwise return raw. US default region matches lead/checkout flows. */
function formattedResponseFieldValue({
  columnId,
  headerLabel,
  rawValue,
}: {
  columnId: string;
  headerLabel: string;
  rawValue: string;
}): string {
  const displayName = headerLabel.trim() || columnId;
  if (!displayName.toLowerCase().includes("phone")) return rawValue;

  const trimmed = rawValue.trim();
  if (!trimmed) return rawValue;

  try {
    const parsed = parsePhoneNumberFromString(trimmed, "US");
    if (parsed?.isValid()) return parsed.number;
  } catch {
    //
  }
  return rawValue;
}

interface ResponsesTabProps {
  pageId?: string;
}

type SortColumn = "date" | "form" | "lead" | string;
type SortDirection = "asc" | "desc";

export function ResponsesTab({ pageId }: ResponsesTabProps) {
  const [responses, setResponses] = useState<PageResponse[]>([]);
  const [page, setPage] = useState<Page | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [replayingId, setReplayingId] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<SortColumn | null>("date");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  useEffect(() => {
    async function fetchData() {
      if (!pageId) return;

      setIsLoading(true);
      try {
        const pageResponse = await fetch(`/api/pages/${pageId}`);
        if (pageResponse.ok) {
          const pageData = await pageResponse.json();
          setPage(pageData.page);
        }

        const responsesResponse = await fetch(`/api/pages/${pageId}/responses`);
        if (responsesResponse.ok) {
          const responsesData = await responsesResponse.json();
          setResponses(responsesData.responses || []);
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, [pageId]);

  const handleReplay = async ({ responseId }: { responseId: string }) => {
    if (!pageId) return;

    setReplayingId(responseId);
    try {
      const response = await fetch(
        `/api/pages/${pageId}/responses/${responseId}/replay`,
        { method: "POST" }
      );

      if (!response.ok) {
        const data = await response.json();
        alert(data.error || "Failed to re-run form actions");
      }
    } catch (error) {
      console.error("Failed to re-run form actions:", error);
      alert("An error occurred while re-running form actions");
    } finally {
      setReplayingId(null);
    }
  };

  const handleDelete = async ({ responseId }: { responseId: string }) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this response? This action cannot be undone."
      )
    ) {
      return;
    }

    if (!pageId) return;

    setDeletingId(responseId);
    try {
      const response = await fetch(
        `/api/pages/${pageId}/responses/${responseId}`,
        { method: "DELETE" }
      );

      if (response.ok) {
        setResponses((prev) => prev.filter((r) => r._id !== responseId));
      } else {
        const data = await response.json();
        alert(data.error || "Failed to delete response");
      }
    } catch (error) {
      console.error("Failed to delete response:", error);
      alert("An error occurred while deleting the response");
    } finally {
      setDeletingId(null);
    }
  };

  const sortedResponses = useMemo(() => {
    if (!sortColumn) return responses;
    const getSortValue = (response: PageResponse): string | number => {
      if (sortColumn === "date") {
        return new Date(response.createdAt).getTime();
      }
      if (sortColumn === "form") {
        return response.formHtmlId ?? "";
      }
      if (sortColumn === "lead") {
        return response.lead ?? "";
      }
      const responseItem = response.responses.find(
        (r) => (r.block || r.htmlId) === sortColumn
      );
      return responseItem?.value ?? "";
    };
    return [...responses].sort((a, b) => {
      const aVal = getSortValue(a);
      const bVal = getSortValue(b);
      const cmp =
        typeof aVal === "number" && typeof bVal === "number"
          ? aVal - bVal
          : String(aVal).localeCompare(String(bVal));
      return sortDirection === "asc" ? cmp : -cmp;
    });
  }, [responses, sortColumn, sortDirection]);

  const { orderedColumnIds, columnHeaders } = useMemo(() => {
    if (!page) {
      return {
        orderedColumnIds: [] as string[],
        columnHeaders: {} as Record<string, string>,
      };
    }

    const columnIds = new Set<string>();
    responses.forEach((response) => {
      response.responses.forEach((r) => {
        const columnId = r.block || r.htmlId;
        if (columnId) columnIds.add(columnId);
      });
    });

    const nextHeaders: Record<string, string> = {};
    columnIds.forEach((columnId) => {
      const firstResponse = responses
        .flatMap((r) => r.responses)
        .find((r) => (r.block || r.htmlId) === columnId);

      if (firstResponse?.htmlId && !firstResponse?.block) {
        nextHeaders[columnId] = firstResponse.htmlId;
      } else if (firstResponse?.block) {
        const withHtmlId = responses
          .flatMap((r) => r.responses)
          .find((r) => r.block === columnId && r.htmlId);
        if (withHtmlId?.htmlId) {
          nextHeaders[columnId] = withHtmlId.htmlId;
        } else {
          const block = findBlockById(page.blocks, columnId);
          nextHeaders[columnId] = block?.data?.label ?? "";
        }
      } else {
        nextHeaders[columnId] = "";
      }
    });

    return {
      orderedColumnIds: Array.from(columnIds),
      columnHeaders: nextHeaders,
    };
  }, [page, responses]);

  const exportCsv = useCallback(() => {
    const fields = [
      "Response ID",
      "Lead",
      "Date (ISO)",
      "Form",
      ...orderedColumnIds.map((id) => {
        const label = columnHeaders[id]?.trim();
        return label || id;
      }),
    ];
    const data = sortedResponses.map((row) => [
      row._id,
      row.lead ?? "",
      new Date(row.createdAt).toISOString(),
      row.formHtmlId ?? "",
      ...orderedColumnIds.map((columnId) => {
        const item = row.responses.find(
          (r) => (r.block || r.htmlId) === columnId
        );
        const raw = item?.value ?? "";
        return formattedResponseFieldValue({
          columnId,
          headerLabel: columnHeaders[columnId] ?? "",
          rawValue: raw,
        });
      }),
    ]);
    const csv = Papa.unparse({ fields, data });
    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const raw =
      page?.title
        ?.trim()
        .slice(0, 40)
        .replace(/\s+/g, "-")
        .replace(/[^a-zA-Z0-9-_]/g, "") ?? "";
    const safeSlug = raw || pageId;
    a.download = `page-responses-${safeSlug}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [
    columnHeaders,
    orderedColumnIds,
    page?.title,
    pageId,
    sortedResponses,
  ]);

  if (!pageId) {
    return (
      <div className="flex-1 overflow-auto p-4">
        <p className="text-sm text-gray-500">No page selected.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex-1 flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  if (!page) {
    return (
      <div className="flex-1 overflow-auto p-4">
        <p className="text-sm text-gray-500">Page not found.</p>
      </div>
    );
  }

  const uniqueLeadCount = new Set(
    responses.map((r) => r.lead).filter(Boolean)
  ).size;

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection(column === "date" ? "desc" : "asc");
    }
  };

  const SortableHead = ({
    column,
    children,
    className,
  }: {
    column: SortColumn;
    children: React.ReactNode;
    className?: string;
  }) => (
    <TableHead
      className={`cursor-pointer hover:bg-muted/50 ${className ?? ""}`}
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortColumn === column ? (
          sortDirection === "asc" ? (
            <ArrowUp className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5 text-muted-foreground" />
          )
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground opacity-50" />
        )}
      </div>
    </TableHead>
  );

  return (
    <div className="flex-1 overflow-auto p-4">
      {responses.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          No responses found for this page.
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <Button type="button" variant="secondary" onClick={exportCsv}>
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>
          <div className="flex flex-wrap gap-4">
            <Card className="w-1/3 min-w-[10rem]">
              <CardContent className="py-4 px-6 text-center">
                <div className="text-2xl font-semibold">{responses.length}</div>
                <div className="text-sm text-muted-foreground">Total</div>
              </CardContent>
            </Card>
            <Card className="w-1/3 min-w-[10rem]">
              <CardContent className="py-4 px-6 text-center">
                <div className="text-2xl font-semibold">{uniqueLeadCount}</div>
                <div className="text-sm text-muted-foreground">Unique</div>
              </CardContent>
            </Card>
          </div>
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <SortableHead column="lead" className="w-12">
                  <User className="h-4 w-4" />
                </SortableHead>
                <SortableHead column="date">Date</SortableHead>
                <SortableHead column="form">Form</SortableHead>
                {orderedColumnIds.map((columnId) => (
                  <SortableHead key={columnId} column={columnId}>
                    {columnHeaders[columnId] || ""}
                  </SortableHead>
                ))}
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedResponses.map((response) => (
                <TableRow key={response._id}>
                  <TableCell>
                    {response.lead ? (
                      <Link
                        to={`/contacts/${response.lead}/edit`}
                        className="text-primary hover:underline inline-flex"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(response.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {response.formHtmlId || "—"}
                  </TableCell>
                  {orderedColumnIds.map((columnId) => {
                    const responseItem = response.responses.find(
                      (r) => (r.block || r.htmlId) === columnId
                    );
                    const raw = responseItem?.value ?? "";
                    return (
                      <TableCell key={columnId}>
                        {formattedResponseFieldValue({
                          columnId,
                          headerLabel: columnHeaders[columnId] ?? "",
                          rawValue: raw,
                        })}
                      </TableCell>
                    );
                  })}
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground"
                          disabled={
                            deletingId === response._id ||
                            replayingId === response._id
                          }
                        >
                          {deletingId === response._id ||
                          replayingId === response._id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <MoreHorizontal className="h-4 w-4" />
                          )}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() =>
                            handleReplay({ responseId: response._id })
                          }
                        >
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Re-run form actions
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() =>
                            handleDelete({ responseId: response._id })
                          }
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        </div>
      )}
    </div>
  );
}
